import React, { useEffect, useMemo, useState } from "react";
import api, { dkk, num, dateDK, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Pencil, RotateCcw, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DeleteCarDialog from "../components/DeleteCarDialog";

const VAT_RATE = 0.25;
const PREP = 3500;
const brugtmoms = (margin) => margin > 0 ? margin * 0.20 : 0;

const fmt = (v) => {
  if (v == null) return "—";
  if (v === 0) return "—";
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(v));
};

export default function Sold() {
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sale_price: "", sale_date: "" });
  const [withVat, setWithVat] = useState(true);
  const [sortKey, setSortKey] = useState("sale_date");
  const [sortDir, setSortDir] = useState("desc");
  const [deleteCar, setDeleteCar] = useState(null);

  const load = () => {
    api.get("/cars", { params: { status_filter: "sold", include_revenue: true } })
      .then((r) => setCars(r.data))
      .catch((e) => toast.error(formatErr(e)));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => cars.map((c) => {
    const sale = Number(c.sale_price || 0);
    const purchase = Number(c.purchase_price || 0);
    // Ekstraomkostninger ex moms
    const extras = (c.extra_costs || []).reduce((s, ec) => {
      const amt = Number(ec.amount || 0);
      return s + (ec.vat_included ? amt / 1.25 : amt);
    }, 0);
    const realCost = purchase ? purchase + PREP + extras : 0;
    const margin = sale - realCost;
    const sale_net = margin - brugtmoms(margin);
    const rentalGross = Number(c.total_rental_revenue || 0);
    const rentalDisplayed = withVat ? rentalGross : rentalGross / (1 + VAT_RATE);
    const totalDisplayed = (withVat ? margin : sale_net) + rentalDisplayed;
    return {
      ...c,
      _sale: sale, _purchase: purchase, _realCost: realCost, _extras: extras,
      _margin: margin, _saleNet: sale_net,
      _rentalGross: rentalGross, _rentalDisplayed: rentalDisplayed,
      _totalDisplayed: totalDisplayed,
    };
  }), [cars, withVat]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "license_plate": av = a.license_plate; bv = b.license_plate; break;
        case "make": av = `${a.make || ""} ${a.model || ""}`; bv = `${b.make || ""} ${b.model || ""}`; break;
        case "sale_date": av = a.sale_date || ""; bv = b.sale_date || ""; break;
        case "sale": av = a._sale; bv = b._sale; break;
        case "purchase": av = a._realCost; bv = b._realCost; break;
        case "margin": av = withVat ? a._margin : a._saleNet; bv = withVat ? b._margin : b._saleNet; break;
        case "rental": av = a._rentalDisplayed; bv = b._rentalDisplayed; break;
        case "total": av = a._totalDisplayed; bv = b._totalDisplayed; break;
        default: av = a.sale_date || ""; bv = b.sale_date || "";
      }
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return ((av || 0) - (bv || 0)) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir, withVat]);

  const totals = useMemo(() => {
    const totalSale = rows.reduce((a, c) => a + c._sale, 0);
    const totalCost = rows.reduce((a, c) => a + c._realCost, 0);
    const totalMargin = rows.reduce((a, c) => a + (withVat ? c._margin : c._saleNet), 0);
    const totalRental = rows.reduce((a, c) => a + c._rentalDisplayed, 0);
    const grand = totalMargin + totalRental;
    return { totalSale, totalCost, totalMargin, totalRental, grand, count: rows.length };
  }, [rows, withVat]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ sale_price: c.sale_price ?? "", sale_date: c.sale_date || "" });
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/cars/${editing.id}`, {
        sale_price: form.sale_price === "" ? null : Number(form.sale_price),
        sale_date: form.sale_date || null,
      });
      toast.success("Opdateret");
      setEditing(null);
      load();
    } catch (e2) { toast.error(formatErr(e2)); }
  };

  const reactivate = async (c) => {
    if (!window.confirm("Sæt bilen tilbage som aktiv?")) return;
    try {
      await api.patch(`/cars/${c.id}`, { status: "active", sale_price: null, sale_date: null });
      toast.success("Bil reaktiveret");
      load();
    } catch (e2) { toast.error(formatErr(e2)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]" data-testid="sold-page">
      <div className="flex items-end justify-between flex-wrap gap-4 sm:gap-6 mb-6 sm:mb-10">
        <div>
          <div className="overline text-zinc-500">Solgte biler</div>
          <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">Arkiv & økonomi.</h1>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={withVat}
            onCheckedChange={setWithVat}
            id="sold-vat"
            data-testid="sold-toggle-vat"
            className="data-[state=checked]:bg-[#C8A661]"
          />
          <Label htmlFor="sold-vat" className="cursor-pointer">
            <div className="font-medium text-sm">{withVat ? "Inkl. moms" : "Ex. moms"}</div>
            <div className="text-xs text-zinc-500">{withVat ? "Brutto" : "Efter brugtmoms"}</div>
          </Label>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800 mb-10 border border-zinc-800">
        <Stat label="Antal" value={num(totals.count)} testid="sold-total-count" />
        <Stat label="Avance" value={dkk(totals.totalMargin)} testid="sold-total-margin" />
        <Stat label="Udlejning" value={dkk(totals.totalRental)} testid="sold-total-rental" />
        <Stat label="Samlet" value={dkk(totals.grand)} testid="sold-grand-total" gold />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="sold-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wider">
              <SortHeader k="license_plate" current={sortKey} dir={sortDir} onClick={toggleSort}>Plade</SortHeader>
              <SortHeader k="make" current={sortKey} dir={sortDir} onClick={toggleSort}>Bil</SortHeader>
              <SortHeader k="sale_date" current={sortKey} dir={sortDir} onClick={toggleSort}>Salgsdato</SortHeader>
              <SortHeader k="sale" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Salg</SortHeader>
              <SortHeader k="purchase" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Reel kost</SortHeader>
              <SortHeader k="margin" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Avance</SortHeader>
              <SortHeader k="rental" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Udlejning</SortHeader>
              <SortHeader k="total" current={sortKey} dir={sortDir} onClick={toggleSort} align="right">Samlet</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && (
              <tr><td colSpan="9" className="p-12 text-center text-zinc-600">Ingen solgte biler endnu.</td></tr>
            )}
            {sortedRows.map((c) => {
              const m = withVat ? c._margin : c._saleNet;
              const has_prices = c.sale_price != null && c.purchase_price != null;
              return (
                <tr key={c.id} className="border-b border-zinc-900 hover:bg-[#1F1F1F] transition-colors" data-testid={`sold-row-${c.license_plate}`}>
                  <td className="font-display font-bold tracking-tight text-white py-3 pr-3">{c.license_plate}</td>
                  <td className="text-zinc-400 py-3 pr-3">{c.make ? `${c.make} ${c.model || ""}` : "—"}</td>
                  <td className="text-zinc-500 py-3 pr-3 text-xs">{dateDK(c.sale_date)}</td>
                  <td className="text-right tabular-nums text-zinc-300 py-3 pr-3">{fmt(c._sale)}</td>
                  <td className="text-right tabular-nums py-3 pr-3">
                    <div className="text-zinc-300">{fmt(c._realCost)}</div>
                    {c._purchase > 0 && (
                      <div className="text-[10px] text-zinc-600">
                        {fmt(c._purchase)} + 3.500{c._extras > 0 ? ` + ${fmt(c._extras)}` : ""}
                      </div>
                    )}
                  </td>
                  <td className={"text-right tabular-nums py-3 pr-3 " + (has_prices ? (m >= 0 ? "text-emerald-400" : "text-red-400") : "text-zinc-700")}>
                    {has_prices ? fmt(m) : "—"}
                  </td>
                  <td className="text-right tabular-nums text-zinc-400 py-3 pr-3">{fmt(c._rentalDisplayed)}</td>
                  <td className={"text-right tabular-nums font-medium py-3 pr-3 " + (c._totalDisplayed >= 0 ? "text-white" : "text-red-400")}>
                    {fmt(c._totalDisplayed)}
                  </td>
                  <td className="text-right whitespace-nowrap py-3">
                    <button onClick={() => openEdit(c)} className="text-zinc-600 hover:text-white p-1.5" data-testid={`sold-edit-${c.license_plate}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => reactivate(c)} className="text-zinc-600 hover:text-white p-1.5 ml-0.5" data-testid={`sold-reactivate-${c.license_plate}`}>
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {sortedRows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-700 font-display">
                <td colSpan={3} className="py-4 text-zinc-500 uppercase tracking-wider text-[10px]">Total</td>
                <td className="text-right tabular-nums text-zinc-300 py-4 pr-3">{fmt(totals.totalSale)}</td>
                <td className="text-right tabular-nums text-zinc-300 py-4 pr-3">{fmt(totals.totalCost)}</td>
                <td className={"text-right tabular-nums py-4 pr-3 " + (totals.totalMargin >= 0 ? "text-emerald-400" : "text-red-400")}>{fmt(totals.totalMargin)}</td>
                <td className="text-right tabular-nums text-zinc-400 py-4 pr-3">{fmt(totals.totalRental)}</td>
                <td className="text-right tabular-nums text-[#C8A661] font-bold py-4 pr-3">{fmt(totals.grand)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-none" data-testid="sold-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tighter text-2xl">
              Salgsdetaljer · {editing?.license_plate}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="overline">Salgspris (DKK)</Label>
              <Input type="number" value={form.sale_price}
                onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                className="rounded-none border-zinc-700 mt-2" data-testid="sold-saleprice-input" />
            </div>
            <div>
              <Label className="overline">Salgsdato</Label>
              <Input type="date" value={form.sale_date}
                onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                className="rounded-none border-zinc-700 mt-2" data-testid="sold-saledate-input" />
            </div>
            <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDeleteCar(editing); setEditing(null); }}
                className="rounded-none border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300"
                data-testid={`delete-car-${editing?.license_plate}`}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Slet bil
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-none border-zinc-700"
                  onClick={() => setEditing(null)}>Annuller</Button>
                <Button type="submit" className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574]"
                  data-testid="sold-save-button">Gem</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteCarDialog
        cars={deleteCar ? [deleteCar] : null}
        onClose={() => setDeleteCar(null)}
        onDeleted={() => { setDeleteCar(null); load(); }}
      />
    </div>
  );
}

function Stat({ label, value, testid, gold }) {
  return (
    <div className="bg-[#161616] p-4 sm:p-6" data-testid={testid}>
      <div className="overline text-zinc-500 mb-2 sm:mb-3 text-[9px] sm:text-xs">{label}</div>
      <div className={"font-display font-black tracking-tighter text-xl sm:text-3xl " + (gold ? "text-[#C8A661]" : "text-white")}>
        {value}
      </div>
    </div>
  );
}

function SortHeader({ k, current, dir, onClick, align = "left", children }) {
  const active = current === k;
  return (
    <th
      className={"cursor-pointer select-none py-3 pr-3 font-normal " + (align === "right" ? "text-right" : "")}
      onClick={() => onClick(k)}
      data-testid={`sort-${k}`}
    >
      <span className={"inline-flex items-center gap-1 " + (active ? "text-[#C8A661]" : "hover:text-zinc-300")}>
        {children}
        {active && (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}
